import React, { useEffect, useMemo, useState } from 'react';
import {
  createTemplate as createTemplateApi,
  deleteTemplate as deleteTemplateApi,
  fetchTemplateLibrary,
  fetchUserTemplates,
  importTemplateFromLibrary,
  updateTemplate as updateTemplateApi
} from '../../services/TemplateService';
import type { TemplateRecord } from '../../services/TemplateService';

type Template = TemplateRecord;

export const TemplatesPage: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [isQuickActionOpen, setIsQuickActionOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateLibrary, setTemplateLibrary] = useState<Template[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [libraryCategoryFilter, setLibraryCategoryFilter] = useState('all');
  
  // Dropdown menu state
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  
  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    content: '',
    category: 'general',
    customCategory: ''
  });
  
  // Template Library search state
  const [librarySearchQuery, setLibrarySearchQuery] = useState('');
  
  const templateCharacterCount = templateContent.length;
  const templateSmsCount = templateCharacterCount === 0 ? 0 : Math.ceil(templateCharacterCount / 160);

  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      setTemplateError(null);
      try {
        const data = await fetchUserTemplates();
        setTemplates(data);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load templates';
        setTemplateError(message);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, []);

  useEffect(() => {
    const loadLibrary = async () => {
      setIsLoadingLibrary(true);
      setLibraryError(null);
      try {
        const data = await fetchTemplateLibrary();
        setTemplateLibrary(data);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load template library';
        setLibraryError(message);
      } finally {
        setIsLoadingLibrary(false);
      }
    };

    loadLibrary();
  }, []);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return templates;
    }

    const query = searchQuery.trim().toLowerCase();
    return templates.filter((template) =>
      template.name.toLowerCase().includes(query) ||
      template.category.toLowerCase().includes(query) ||
      template.content?.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  const filteredLibraryTemplates = useMemo(() => {
    const query = librarySearchQuery.trim().toLowerCase();

    return templateLibrary.filter((template) => {
      const matchesQuery =
        !query ||
        template.name.toLowerCase().includes(query) ||
        template.content?.toLowerCase().includes(query);
      const matchesCategory =
        libraryCategoryFilter === 'all' || template.category.toLowerCase() === libraryCategoryFilter;

      return matchesQuery && matchesCategory;
    });
  }, [templateLibrary, librarySearchQuery, libraryCategoryFilter]);

  const resetCreateForm = () => {
    setTemplateName('');
    setTemplateContent('');
    setSelectedCategory('general');
    setShowCustomCategory(false);
    setCustomCategory('');
  };

  const handleCreateTemplate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTemplateError(null);

    if (!templateName.trim()) {
      setTemplateError('Template name is required');
      return;
    }

    if (!templateContent.trim()) {
      setTemplateError('Template content is required');
      return;
    }

    if (selectedCategory === 'custom' && !customCategory.trim()) {
      setTemplateError('Please provide a custom category name');
      return;
    }

    const categoryValue =
      selectedCategory === 'custom' ? customCategory.trim() : selectedCategory;

    setIsCreatingTemplate(true);

    try {
      const created = await createTemplateApi({
        name: templateName.trim(),
        content: templateContent.trim(),
        category: categoryValue,
        customCategory: selectedCategory === 'custom' ? customCategory.trim() : undefined
      });

      setTemplates((prev) => [created, ...prev]);
      resetCreateForm();
      // Show the templates list after successful creation
      setShowCreateForm(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create template';
      setTemplateError(message);
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === 'custom') {
      setShowCustomCategory(true);
      setSelectedCategory('custom');
    } else {
      setShowCustomCategory(false);
      setSelectedCategory(selectedValue);
      setCustomCategory('');
    }
  };

  // Toggle dropdown menu
  const toggleDropdown = (id: string) => {
    if (activeDropdownId === id) {
      setActiveDropdownId(null);
    } else {
      setActiveDropdownId(id);
    }
  };
  
  // Open edit modal
  const openEditModal = (template: Template) => {
    const predefinedCategories = ['general', 'marketing', 'sales', 'support', 'notification', 'reminder'];
    const templateCategoryLower = template.category.toLowerCase();
    const isCustomCategory = !predefinedCategories.includes(templateCategoryLower);
    setEditingTemplate(template);
    setEditForm({
      name: template.name,
      content: template.content,
      category: isCustomCategory ? 'custom' : templateCategoryLower,
      customCategory: isCustomCategory ? template.category : ''
    });
    setIsEditModalOpen(true);
    setActiveDropdownId(null);
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTemplate(null);
  };
  
  // Handle edit form changes
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle edit form submission
  const handleEditSubmit = async () => {
    if (!editingTemplate) {
      return;
    }

    setTemplateError(null);

    if (!editForm.name.trim()) {
      setTemplateError('Template name is required');
      return;
    }

    if (!editForm.content.trim()) {
      setTemplateError('Template content is required');
      return;
    }

    if (editForm.category === 'custom' && !editForm.customCategory.trim()) {
      setTemplateError('Please provide a custom category name');
      return;
    }

    const categoryValue =
      editForm.category === 'custom'
        ? editForm.customCategory.trim() || editingTemplate.category
        : editForm.category;

    setIsUpdatingTemplate(true);

    try {
      const updated = await updateTemplateApi(editingTemplate.id, {
        name: editForm.name.trim(),
        content: editForm.content,
        category: categoryValue,
        customCategory: editForm.category === 'custom' ? editForm.customCategory.trim() || undefined : undefined
      });

      setTemplates((prev) =>
        prev.map((template) => (template.id === updated.id ? updated : template))
      );

      closeEditModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update template';
      setTemplateError(message);
    } finally {
      setIsUpdatingTemplate(false);
    }
  };
  
  // Handle edit category change
  const handleEditCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === 'custom') {
      setEditForm(prev => ({
        ...prev,
        category: 'custom',
        customCategory: prev.customCategory
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        category: selectedValue,
        customCategory: ''
      }));
    }
  };
  
  // Handle edit action
  const handleEdit = (template: Template) => {
    openEditModal(template);
  };
  
  // Open delete confirmation modal
  const openDeleteModal = (template: Template) => {
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
    setActiveDropdownId(null);
  };
  
  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };
  
  // Handle delete confirmation
  const confirmDelete = async () => {
    if (!templateToDelete) {
      return;
    }

    setTemplateError(null);
    setIsDeletingTemplate(true);

    try {
      await deleteTemplateApi(templateToDelete.id);
      setTemplates((prev) => prev.filter((template) => template.id !== templateToDelete.id));
      setSelectedTemplates((prev) => prev.filter((id) => id !== templateToDelete.id));
      closeDeleteModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete template';
      setTemplateError(message);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  // Add template to user's list function
  const addTemplateToUserList = async (template: Template) => {
    setTemplateError(null);

    try {
      const imported = await importTemplateFromLibrary(template.id);
      setTemplates((prev) => [imported, ...prev]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add template from library';
      setTemplateError(message);
    } finally {
      setActiveDropdownId(null);
    }
  };

  const templatesCount = filteredTemplates.length;
  const libraryCount = filteredLibraryTemplates.length;

  return (
    <div className="space-y-6">
      {showCreateForm ? (
        <>
          {/* Header Banner */}
          <div className="bg-white border-b">
            <div className="px-6 py-4">
              <h1 className="text-xl font-bold">Create new template</h1>
            </div>
          </div>

          {templateError && (
            <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {templateError}
            </div>
          )}

          {/* Create Template Form */}
          <div className="max-w-5xl mx-auto bg-white rounded-lg shadow mb-8">
        <form className="p-6" onSubmit={handleCreateTemplate}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Form Fields */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                >
                  <option value="general">General</option>
                  <option value="marketing">Marketing</option>
                  <option value="sales">Sales</option>
                  <option value="support">Support</option>
                  <option value="notification">Notification</option>
                  <option value="reminder">Reminder</option>
                  <option value="custom">Custom</option>
                </select>
                
                {showCustomCategory && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Category Name
                    </label>
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category name"
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message content
                </label>
                <textarea
                  id="template-content-textarea"
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  placeholder="Enter message content"
                  rows={8}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
                <div className="mt-2 text-sm text-gray-500 flex justify-between">
                  <span>Total symbols: {templateCharacterCount}</span>
                  <span>
                    {templateSmsCount} SMS message{templateSmsCount === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column - Message Variables */}
            <div className="space-y-4">
              {/* Message Variables Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Variables
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  onChange={(e) => {
                    if (e.target.value) {
                      const textArea = document.getElementById('template-content-textarea') as HTMLTextAreaElement;
                      if (textArea) {
                        const start = textArea.selectionStart;
                        const end = textArea.selectionEnd;
                        const text = textArea.value;
                        const newText = text.substring(0, start) + e.target.value + text.substring(end);
                        setTemplateContent(newText);
                        // Set cursor position after inserted variable
                        setTimeout(() => {
                          textArea.focus();
                          textArea.setSelectionRange(start + e.target.value.length, start + e.target.value.length);
                        }, 0);
                      }
                    }
                    e.target.value = ''; // Reset selection
                  }}
                >
                  <option value="">Select variable</option>
                  <option value="{NAME}">Name</option>
                  <option value="{SURNAME}">Surname</option>
                  <option value="{PHONE}">Phone number</option>
                  <option value="{EMAIL}">Email</option>
                  <option value="{DATE}">Date</option>                                
                  <option value="{URL}">URL</option>                
                  <option value="{CUSTM1}">Custom field</option>
                </select>
              </div>

              {/* Total Symbols Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total characters in SMS message
                </label>
                <div className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50">
                  <span className="text-sm text-gray-600">Characters used:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{templateCharacterCount}</span>
                    <span className="text-sm text-gray-500">/ 160</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({templateSmsCount} SMS)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6 mt-6 border-t">
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              disabled={isCreatingTemplate}
              className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back to Templates List
            </button>
            <button
              type="submit"
              disabled={isCreatingTemplate}
              className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingTemplate ? 'Creating...' : 'Create template'}
            </button>
          </div>
        </form>
      </div>
        </>
      ) : (
        <>
          {/* Templates List Banner */}
          <div className="bg-white border-b">
            <div className="px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Templates List</h2>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>New Template</span>
              </button>
            </div>
          </div>

          {templateError && (
            <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {templateError}
            </div>
          )}

          {/* Templates List Section */}
          <div className="space-y-6 rounded-lg p-6">
        {/* Actions Row */}
        <div className="flex justify-between items-center">
          {/* Quick Action Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsQuickActionOpen(!isQuickActionOpen)}
              className="flex items-center space-x-2 bg-white border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
            >
              <span>Quick action</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isQuickActionOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                <div className="py-1">
                  <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Delete selected
                  </button>
                  <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Export templates
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Search Box */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
            <svg 
              className="w-4 h-4 text-gray-400 absolute left-3 top-3"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Templates Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="w-12 py-3 px-4">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                  />
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Template name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Category</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Characters</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">SMS count</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Last used</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingTemplates ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">Loading templates...</td>
                </tr>
              ) : filteredTemplates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">No templates found</td>
                </tr>
              ) : (
                filteredTemplates.map((template) => (
                  <tr key={template.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedTemplates.includes(template.id)}
                        onChange={() => {
                          if (selectedTemplates.includes(template.id)) {
                            setSelectedTemplates(selectedTemplates.filter(id => id !== template.id));
                          } else {
                            setSelectedTemplates([...selectedTemplates, template.id]);
                          }
                        }}
                        className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                      />
                    </td>
                    <td className="py-3 px-4">{template.name}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {template.category}
                      </span>
                    </td>
                    <td className="py-3 px-4">{template.characters}</td>
                    <td className="py-3 px-4">{template.smsCount}</td>
                    <td className="py-3 px-4">{template.lastUsed}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(template)}
                          className="p-1.5 text-gray-600 hover:text-[#FF5722] hover:bg-orange-50 rounded transition-colors duration-200"
                          title="Edit template"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openDeleteModal(template)}
                          className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200"
                          title="Delete template"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center space-x-2">
              <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm text-gray-600">{templatesCount} template{templatesCount === 1 ? '' : 's'}</span>
              <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Show rows</span>
              <select className="p-1 text-sm border rounded">
                <option>10</option>
                <option>25</option>
                <option>50</option>
                <option>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Template Library Section */}
      <div className="space-y-6 p-6">
        {/* Template Library Banner */}
        <div className="bg-white border-b">
          <div className="px-6 py-4">
            <h2 className="text-xl font-bold">Template Library</h2>
            <p className="text-sm text-gray-500 mt-1">
              Pre-approved templates from the Super Administrator that you can use in your campaigns
            </p>
          </div>
        </div>
        
        {libraryError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mx-6">
            {libraryError}
          </div>
        )}

        {/* Template Library Search */}
        <div className="flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search library templates..."
              value={librarySearchQuery}
              onChange={(e) => setLibrarySearchQuery(e.target.value)}
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            />
            <svg 
              className="w-4 h-4 text-gray-400 absolute left-3 top-3"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Filter by:</span>
            <select
              value={libraryCategoryFilter}
              onChange={(e) => setLibraryCategoryFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="general">General</option>
              <option value="marketing">Marketing</option>
              <option value="sales">Sales</option>
              <option value="support">Support</option>
              <option value="notification">Notification</option>
              <option value="reminder">Reminder</option>
            </select>
          </div>
        </div>
        
        {/* Template Library Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="w-12 py-3 px-4">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                  />
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Template name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Category</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Characters</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">SMS count</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Last updated</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {isLoadingLibrary ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">Loading template library...</td>
                </tr>
              ) : filteredLibraryTemplates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500">No library templates found</td>
                </tr>
              ) : (
                filteredLibraryTemplates.map((template) => (
                  <tr key={template.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-[#FF5722] focus:ring-[#FF5722]"
                      />
                    </td>
                    <td className="py-3 px-4">{template.name}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {template.category}
                      </span>
                    </td>
                    <td className="py-3 px-4">{template.characters}</td>
                    <td className="py-3 px-4">{template.smsCount}</td>
                    <td className="py-3 px-4">{template.lastUsed}</td>
                    <td className="py-3 px-4">
                      <div className="relative">
                        <button 
                          className="text-gray-400 hover:text-gray-600"
                          onClick={() => toggleDropdown(template.id)}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 14a2 2 0 100-4 2 2 0 000 4zM19 14a2 2 0 100-4 2 2 0 000 4zM5 14a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                        
                        {activeDropdownId === template.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <div className="py-1">
                              <button 
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={() => addTemplateToUserList(template)}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add to My Templates
                              </button>
                              <button 
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={() => {
                                  // Preview functionality would go here
                                  setActiveDropdownId(null);
                                }}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Preview
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center space-x-2">
              <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm text-gray-600">{libraryCount} template{libraryCount === 1 ? '' : 's'}</span>
              <button className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Show rows</span>
              <select className="p-1 text-sm border rounded">
                <option>10</option>
                <option>25</option>
                <option>50</option>
                <option>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
      
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-orange-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-[#FF5722]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Delete Template</h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete the template "{templateToDelete.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeletingTemplate}
                  className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingTemplate ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Template Modal */}
      {isEditModalOpen && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Template</h3>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditFormChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    name="category"
                    value={editForm.category}
                    onChange={handleEditCategoryChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  >
                    <option value="general">General</option>
                    <option value="marketing">Marketing</option>
                    <option value="sales">Sales</option>
                    <option value="support">Support</option>
                    <option value="notification">Notification</option>
                    <option value="reminder">Reminder</option>
                    <option value="custom">Custom</option>
                  </select>
                  
                  {editForm.category === 'custom' && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Category Name
                      </label>
                      <input
                        type="text"
                        name="customCategory"
                        value={editForm.customCategory}
                        onChange={handleEditFormChange}
                        placeholder="Enter custom category name"
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message content
                  </label>
                  <textarea
                    name="content"
                    value={editForm.content}
                    onChange={handleEditFormChange}
                    rows={4}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEditSubmit}
                    disabled={isUpdatingTemplate}
                    className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingTemplate ? 'Updating...' : 'Update Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage; 
